import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { Card, CardBody } from "reactstrap";

import { H4, H5 } from "../../../../AbstractElements";
import SimpleAccordion from "../../../../CommonElements/Accordion";
import ModuleQuestion from "../../../../CommonElements/ModuleQuestion";
import StarLikertScale from "../../../../CommonElements/StarLikertScale";
import StarRating from "../../../../CommonElements/StarRating";
import { useAppDispatch, useAppSelector } from "../../../../ReduxToolkit/Hooks";
import {
  getAllQuestionsAndModules,
  getQuestionAndOptionByModule,
} from "../../../../ReduxToolkit/Reducers/QuestionBankSlice";
import { CorrectAnswer, LoadMore, Options } from "../../../../utils/Constant";
import { questionTypes } from "../../../../utils/helper/helper";

const QuestionBankPreview = () => {
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { questionAndModuleList } = useAppSelector(state => state.questionBank);
  const [moduleList, setModuleList] = useState([]);
  const [moduleQuestions, setModuleQuestions] = useState<
    Record<
      string,
      { questions: any[]; page: number; total: number; module_name: string }
    >
  >({});
  const { questionBankId, courseId, selectedModules } = location.state || {};

  useEffect(() => {
    if (questionBankId) {
      dispatch(getAllQuestionsAndModules({ question_bank_id: questionBankId }));
    }
  }, [dispatch, questionBankId]);

  useEffect(() => {
    if (questionAndModuleList?.modules?.length) {
      const newModuleQuestions = {};
      questionAndModuleList.modules.forEach(mod => {
        if (!selectedModules?.includes(mod?.id)) return;
        newModuleQuestions[mod.id] = {
          questions: mod.questions || [],
          page: 1,
          total:
            mod.question_count || (mod.questions ? mod.questions.length : 0),
          module_name: mod.module_name,
        };
      });
      setModuleQuestions(newModuleQuestions);
    }
  }, [questionAndModuleList, selectedModules]);

  const handleLoadMore = async moduleId => {
    const modState = moduleQuestions[moduleId];
    const nextPage = modState.page + 1;
    const res = await dispatch(
      getQuestionAndOptionByModule({
        module_id: moduleId.toString(),
        page: nextPage,
      })
    );
    const newQuestions = res?.payload?.questions || [];
    setModuleQuestions(prev => ({
      ...prev,
      [moduleId]: {
        ...prev[moduleId],
        questions: [...prev[moduleId].questions, ...newQuestions],
        page: nextPage,
        total: res?.payload?.question_count || prev[moduleId].total,
      },
    }));
  };

  const renderOptions = (options = []) =>
    options.length
      ? options.map((opt, index) => (
          <div key={index}>
            <b>{String.fromCharCode(97 + index)})</b> {opt.option_text}
          </div>
        ))
      : "-";

  const renderCorrectAnswer = item => {
    const { correct_answer, options = [], question_type } = item;

    if (
      question_type === questionTypes.ranking &&
      correct_answer?.options?.length
    ) {
      return (
        <StarRating
          size={24}
          count={options.length || 6}
          initialValue={
            options.findIndex(opt => opt.id === correct_answer.options[0]) + 1
          }
          disabled
        />
      );
    }

    if (
      question_type === questionTypes.match_the_following &&
      correct_answer?.pairs
    ) {
      return Object.entries(correct_answer.pairs).map(([leftId, rightId]) => {
        const left = options.find(opt => opt.id === Number(leftId));
        const right = options.find(opt => opt.id === rightId);
        return (
          <div key={leftId}>
            {left?.option_text || ""} - {right?.option_text || ""}
          </div>
        );
      });
    }

    if (correct_answer?.date) return correct_answer.date;

    return (
      options
        .filter(opt => opt.is_correct)
        .map(opt => opt.option_text)
        .join(", ") || "-"
    );
  };

  const mappedModules = useMemo(() => {
    const modules = [];
    Object.entries(moduleQuestions).forEach(([modId, modState]) => {
      const questions = modState.questions || [];
      const total = modState.total || 0;
      const module_name = modState.module_name;
      const bodyContent = (
        <>
          {questions.map((item, index) => (
            <div key={item?.id}>
              <ModuleQuestion
                showIndicator={false}
                id={item?.id}
                type={item?.question_type}
                status=""
              >
                <H5>{`${index + 1}. ${item?.question_text}`}</H5>
                {[
                  questionTypes.rating,
                  questionTypes.date_picker,
                  questionTypes.likert_scale,
                  questionTypes.match_the_following,
                  questionTypes.open_text,
                ].includes(item?.question_type) === false && (
                  <H5 className="mt-1">
                    <b>{Options} : </b>
                    {renderOptions(item?.options)}
                  </H5>
                )}
                {item?.question_type === questionTypes.likert_scale && (
                  <StarLikertScale
                    value={
                      item.options.findIndex(
                        opt => opt.id === item.correct_answer.options[0]
                      ) + 1
                    }
                    disabled
                    options={item.options.map(opt => opt.option_text)}
                  />
                )}
                <H5 className="mt-1">
                  <b>{CorrectAnswer} : </b>
                  {renderCorrectAnswer(item)}
                </H5>
              </ModuleQuestion>
            </div>
          ))}
          {questions.length < total && (
            <div className="d-flex justify-content-center mt-2">
              <button
                className="btn btn-outline-primary"
                onClick={() => handleLoadMore(modId)}
              >
                {LoadMore}
              </button>
            </div>
          )}
        </>
      );
      modules.push({
        id: modId,
        icon: courseId,
        accordionHeading: <H4>{module_name}</H4>,
        spanClass: "custom-skillUpdate-heading",
        bodyText: bodyContent,
      });
    });
    return modules;
  }, [moduleQuestions, courseId]);

  useEffect(() => {
    setModuleList(mappedModules);
  }, [mappedModules]);

  return (
    <div className="page-body pb-5">
      <Card className="p-2">
        <CardBody>
          <H4>{questionAndModuleList?.name}</H4>
        </CardBody>
      </Card>

      {moduleList?.length > 0 && (
        <div className="mt-3">
          <SimpleAccordion
            showNoOfQuestions={false}
            accordionList={moduleList}
            showDropdown={false}
          />
        </div>
      )}
    </div>
  );
};

export default QuestionBankPreview;
